// ==========================================
// THE VISA CO — CAREERS PAGE CONSTANTS
// ==========================================

import { Globe, Zap, Users, Lightbulb, Heart, TrendingUp, Wifi, Plane, BookOpen, Shield, DollarSign, Gift, Clock, Building2, Check } from "lucide-react";

export const COMPANY_NAME = "The Visa Co";
export const COMPANY_EMAIL = "careers@thevisaco.com";

export const ICONS = {
  globe: Globe,
  zap: Zap,
  users: Users,
  lightbulb: Lightbulb,
  heart: Heart,
  trendingUp: TrendingUp,
  wifi: Wifi,
  plane: Plane,
  book: BookOpen,
  shield: Shield,
  dollar: DollarSign,
  gift: Gift,
  clock: Clock,
  building: Building2,
  check: Check,
};

// ---- DEPARTMENTS ----
export const DEPARTMENTS = [
  { id: "all", label: "All Departments", count: null },
  { id: "data", label: "Data", count: 3 },
  { id: "engineering", label: "Engineering", count: 6 },
  { id: "fulfillment", label: "Fulfillment", count: 1 },
  { id: "growth", label: "Growth", count: 4 },
  { id: "product", label: "Product", count: 2 },
  { id: "guest-delight", label: "Guest Delight", count: 3 },
  { id: "marketing", label: "Marketing", count: 1 },
  
];

// ---- JOB LISTINGS ----
export const JOB_LISTINGS = [
  // Data
  {
    id: "d001",
    title: "Senior Data Analyst",
    department: "data",
    departmentLabel: "Data",
    location: "Remote / Dubai",
    type: "Full-time",
    experience: "4–6 years",
    postedDate: "2025-06-01",
    description: "Join our data team to transform complex datasets into clear, actionable insights that drive strategic business decisions. You'll work closely with cross-functional teams to build dashboards, run analysis, and shape data culture at The Visa Co.",
    requirements: "Proficiency in SQL, Python, and BI tools (Tableau/Looker). Strong analytical and communication skills. Experience in a fast-paced startup environment preferred.",
    role: "As a Senior Data Analyst, you will own end-to-end data pipelines, develop KPI frameworks, and partner with leadership on critical business questions. You'll mentor junior analysts and contribute to establishing best practices.",
    idealCandidate: "You are intellectually curious, detail-oriented, and passionate about turning raw data into stories. You thrive in ambiguity and can move fast without sacrificing accuracy.",
  },
  {
    id: "d002",
    title: "Data Engineer",
    department: "data",
    departmentLabel: "Data",
    location: "Remote / London",
    type: "Full-time",
    experience: "3–5 years",
    postedDate: "2025-05-28",
    description: "Build and maintain robust data infrastructure that powers The Visa Co's analytics and ML capabilities. You'll design pipelines, ensure data quality, and work at the intersection of engineering and analytics.",
    requirements: "Strong experience with dbt, Airflow, Spark, and cloud platforms (GCP/AWS). Python and SQL mastery. Experience with data warehousing (BigQuery, Snowflake, Redshift).",
    role: "Design and deploy scalable ETL/ELT pipelines, manage our data warehouse, ensure data reliability, and enable self-service analytics for the business.",
    idealCandidate: "You take ownership of complex systems, love solving hard data infrastructure problems, and are committed to engineering best practices.",
  },
  {
    id: "d003",
    title: "Business Intelligence Analyst",
    department: "data",
    departmentLabel: "Data",
    location: "Remote / Singapore",
    type: "Full-time",
    experience: "2–4 years",
    postedDate: "2025-05-20",
    description: "Own the reporting and dashboarding layer for The Visa Co. You'll create compelling visuals that give leadership and operations teams the information they need to make smart decisions, fast.",
    requirements: "Proficiency in Looker, Metabase, or Tableau. SQL expertise. Ability to translate business questions into clear metrics and reports.",
    role: "Design and maintain executive and operational dashboards, build self-service reporting tools, and partner with stakeholders to identify their analytics needs.",
    idealCandidate: "You have a keen eye for data storytelling, are a natural communicator, and love making complex data simple and beautiful.",
  },

  // Engineering
  {
    id: "e001",
    title: "Senior Backend Engineer",
    department: "engineering",
    departmentLabel: "Engineering",
    location: "Remote / Global",
    type: "Full-time",
    experience: "5–8 years",
    postedDate: "2025-06-03",
    description: "Design and build the core systems that power The Visa Co's platform. You'll architect scalable APIs, optimize performance, and work on complex distributed systems that handle mission-critical workflows.",
    requirements: "Deep expertise in Node.js or Python. Experience with microservices, REST/GraphQL APIs, PostgreSQL, and Redis. Familiarity with cloud infrastructure (AWS/GCP) and CI/CD practices.",
    role: "Lead the design and implementation of backend services, mentor engineers, participate in architecture decisions, and maintain high reliability standards.",
    idealCandidate: "You are a systems thinker who writes clean, maintainable code, champions engineering excellence, and enjoys mentoring a growing team.",
  },
  {
    id: "e002",
    title: "Full Stack Engineer",
    department: "engineering",
    departmentLabel: "Engineering",
    location: "Remote / Dubai",
    type: "Full-time",
    experience: "3–5 years",
    postedDate: "2025-06-02",
    description: "Work across the entire stack to build beautiful, high-performance product experiences for both customers and internal teams. You'll ship features end-to-end and have real ownership over what you build.",
    requirements: "Proficiency in React and TypeScript on the frontend, Node.js or Python on the backend. Experience with cloud platforms, databases, and deployment pipelines.",
    role: "Collaborate with product and design to implement new features, maintain and improve existing systems, and contribute to technical strategy.",
    idealCandidate: "You love building things people use, care deeply about UX, and are comfortable switching between frontend and backend work.",
  },
  {
    id: "e003",
    title: "DevOps / Platform Engineer",
    department: "engineering",
    departmentLabel: "Engineering",
    location: "Remote / London",
    type: "Full-time",
    experience: "4–6 years",
    postedDate: "2025-05-30",
    description: "Own and evolve The Visa Co's cloud infrastructure, deployment pipelines, and reliability practices. You'll ensure our systems are scalable, secure, and highly available as we grow rapidly.",
    requirements: "Strong Kubernetes, Terraform, and CI/CD expertise. Deep AWS or GCP knowledge. Experience with monitoring, alerting, and incident response.",
    role: "Design and manage cloud infrastructure, build automation tooling, lead incident response, and enable engineering teams to deploy fast and safely.",
    idealCandidate: "You are passionate about reliability and developer experience. You love building the platform that lets other engineers do their best work.",
  },
  {
    id: "e004",
    title: "Frontend Engineer",
    department: "engineering",
    departmentLabel: "Engineering",
    location: "Remote / Global",
    type: "Full-time",
    experience: "3–5 years",
    postedDate: "2025-05-25",
    description: "Build world-class web interfaces that delight our customers. You'll own the frontend architecture of our customer-facing applications and work closely with design to translate pixels into elegant, performant code.",
    requirements: "Expert-level React and TypeScript skills. Strong understanding of web performance, accessibility, and component-driven development. Experience with design systems.",
    role: "Develop and maintain our customer portal and internal tools, establish frontend best practices, and advocate for exceptional user experience.",
    idealCandidate: "You have a strong visual sensibility, obsess over performance and accessibility, and enjoy building UIs that feel magical.",
  },
  {
    id: "e005",
    title: "Mobile Engineer (React Native)",
    department: "engineering",
    departmentLabel: "Engineering",
    location: "Remote / Singapore",
    type: "Full-time",
    experience: "3–5 years",
    postedDate: "2025-05-18",
    description: "Build and maintain The Visa Co's mobile experience for iOS and Android using React Native. You'll work on features that millions of users interact with daily.",
    requirements: "Strong React Native expertise. Experience with native modules, push notifications, deep linking, and app store deployment processes.",
    role: "Develop new mobile features, maintain and improve existing functionality, work with backend engineers on API integration, and improve app performance.",
    idealCandidate: "You care deeply about mobile UX, have a sharp eye for performance issues, and are passionate about shipping high-quality mobile experiences.",
  },
  {
    id: "e006",
    title: "Security Engineer",
    department: "engineering",
    departmentLabel: "Engineering",
    location: "Remote / Dubai",
    type: "Full-time",
    experience: "4–7 years",
    postedDate: "2025-05-15",
    description: "Own the security posture of The Visa Co's platform, customer data, and infrastructure. You'll conduct threat modeling, vulnerability assessments, and work with engineering teams to build security into everything we ship.",
    requirements: "Experience with application security, cloud security (AWS/GCP), penetration testing, SIEM tools, and compliance frameworks (SOC 2, ISO 27001).",
    role: "Lead security reviews, design and implement security controls, respond to incidents, and champion a security-first culture across engineering.",
    idealCandidate: "You are proactive, detail-oriented, and passionate about protecting users and data. You communicate security risks clearly to non-technical stakeholders.",
  },

  // Fulfillment
  {
    id: "f001",
    title: "Visa Fulfillment Specialist",
    department: "fulfillment",
    departmentLabel: "Fulfillment",
    location: "Dubai, UAE",
    type: "Full-time",
    experience: "1–3 years",
    postedDate: "2025-06-01",
    description: "Manage end-to-end visa application processing, coordinate with embassies and government authorities, and ensure customers receive their visas on time with a seamless experience.",
    requirements: "Experience in visa processing, immigration documentation, or travel operations. Attention to detail and ability to manage multiple cases simultaneously. Proficiency in document verification.",
    role: "Process visa applications from start to finish, communicate with clients on status updates, resolve issues with embassy submissions, and maintain accurate records.",
    idealCandidate: "You are highly organised, thrive under deadlines, and take pride in getting things right. You care about the impact your work has on customers' travel plans.",
  },

  // Growth
  {
    id: "g001",
    title: "Head of Growth",
    department: "growth",
    departmentLabel: "Growth",
    location: "Dubai, UAE",
    type: "Full-time",
    experience: "7+ years",
    postedDate: "2025-06-02",
    description: "Lead The Visa Co's growth function and build the engine that drives customer acquisition, retention, and revenue. This is a senior leadership role with full ownership of growth strategy and execution.",
    requirements: "Proven track record in growth marketing, performance marketing, or revenue leadership at a high-growth startup. Strong analytical skills and data-driven decision making.",
    role: "Define and execute growth strategy, own revenue targets, manage and scale the growth team, and align marketing and product on acquisition and retention goals.",
    idealCandidate: "You are a hands-on leader who combines creativity with rigorous analysis. You love running experiments, scaling what works, and building high-performing teams.",
  },
  {
    id: "g002",
    title: "Growth Marketing Manager",
    department: "growth",
    departmentLabel: "Growth",
    location: "Remote / Dubai",
    type: "Full-time",
    experience: "3–5 years",
    postedDate: "2025-05-29",
    description: "Own The Visa Co's paid and organic customer acquisition channels. You'll design and execute campaigns that drive qualified traffic, improve conversion, and grow our customer base efficiently.",
    requirements: "Strong performance marketing experience across Google, Meta, and other paid channels. Proficiency with analytics platforms (GA4, Mixpanel, Amplitude). SEO and CRO knowledge.",
    role: "Plan and execute multi-channel campaigns, optimize spend for CAC and ROAS, run A/B tests, and collaborate with content, product, and design teams.",
    idealCandidate: "You are data-obsessed, creative in your approach to acquisition, and comfortable managing significant ad budgets with accountability.",
  },
  {
    id: "g003",
    title: "CRM & Lifecycle Marketing Specialist",
    department: "growth",
    departmentLabel: "Growth",
    location: "Remote / London",
    type: "Full-time",
    experience: "2–4 years",
    postedDate: "2025-05-22",
    description: "Own the customer lifecycle journey at The Visa Co — from onboarding to retention and reactivation. You'll craft personalised communication strategies that keep customers engaged and loyal.",
    requirements: "Experience with CRM platforms (HubSpot, Braze, or Klaviyo). Strong copywriting skills and ability to build automated email/SMS/push workflows. Analytical mindset.",
    role: "Build and optimise lifecycle email and in-app campaigns, develop customer segmentation strategies, and improve retention metrics across cohorts.",
    idealCandidate: "You are empathetic, data-driven, and love crafting communications that feel personal and timely. You care about the full customer journey.",
  },
  {
    id: "g004",
    title: "Partnerships & BD Manager",
    department: "growth",
    departmentLabel: "Growth",
    location: "Dubai, UAE",
    type: "Full-time",
    experience: "3–6 years",
    postedDate: "2025-05-10",
    description: "Identify, develop, and manage strategic partnerships that accelerate The Visa Co's growth. You'll work with travel agencies, corporate partners, and B2B channels to expand our reach.",
    requirements: "Proven experience in business development, partnerships, or enterprise sales. Strong network in the travel, hospitality, or fintech space preferred. Excellent negotiation and communication skills.",
    role: "Source and close partnership deals, manage partner relationships, develop joint go-to-market strategies, and represent The Visa Co at industry events.",
    idealCandidate: "You are a natural relationship builder, commercially savvy, and driven by the thrill of closing deals that create long-term mutual value.",
  },

  // Guest Delight
  {
    id: "gd001",
    title: "Guest Delight Lead",
    department: "guest-delight",
    departmentLabel: "Guest Delight",
    location: "Dubai, UAE",
    type: "Full-time",
    experience: "4–6 years",
    postedDate: "2025-05-31",
    description: "Lead The Visa Co's customer support team and own the end-to-end customer experience. You'll build processes, coach the team, and champion a culture of genuine care and delight.",
    requirements: "Experience leading customer support teams in a fast-paced environment. Strong process design and people management skills. Familiarity with support tools (Zendesk, Intercom, Freshdesk).",
    role: "Manage and mentor the Guest Delight team, design support workflows, own CSAT and NPS targets, and escalate systemic issues to product and operations.",
    idealCandidate: "You genuinely care about customers, lead with empathy, and have a passion for building teams that deliver exceptional service at scale.",
  },
  {
    id: "gd002",
    title: "Guest Delight Specialist",
    department: "guest-delight",
    departmentLabel: "Guest Delight",
    location: "Remote / Dubai",
    type: "Full-time",
    experience: "1–3 years",
    postedDate: "2025-05-26",
    description: "Be the friendly, knowledgeable face of The Visa Co for our customers. You'll handle inquiries across chat, email, and phone, resolve issues with care, and ensure every customer feels valued.",
    requirements: "Strong communication skills, both written and verbal. Experience in customer support or hospitality. Patient, empathetic, and solutions-focused mindset. Multilingual is a plus.",
    role: "Handle incoming customer queries, process escalations, maintain accurate case records, and identify trends to improve the overall customer experience.",
    idealCandidate: "You are a natural people person who finds satisfaction in helping others. You stay calm under pressure and take ownership of every customer interaction.",
  },
  {
    id: "gd003",
    title: "Quality Assurance & Training Analyst",
    department: "guest-delight",
    departmentLabel: "Guest Delight",
    location: "Remote / London",
    type: "Full-time",
    experience: "2–4 years",
    postedDate: "2025-05-12",
    description: "Ensure the highest standards of customer service at The Visa Co by designing QA frameworks, conducting audits, and delivering training programmes that level up the Guest Delight team.",
    requirements: "Experience in QA or training roles within a customer support function. Strong feedback and coaching skills. Data-driven approach to quality assessment.",
    role: "Develop and implement QA scorecards, conduct regular audits of customer interactions, deliver onboarding and upskilling training, and report quality metrics to leadership.",
    idealCandidate: "You are detail-oriented, a natural teacher, and passionate about continuous improvement. You see every interaction as an opportunity to raise the bar.",
  },

  // Marketing
  {
    id: "m001",
    title: "Content & Brand Marketing Manager",
    department: "marketing",
    departmentLabel: "Marketing",
    location: "Remote / Dubai",
    type: "Full-time",
    experience: "3–5 years",
    postedDate: "2025-05-27",
    description: "Define and execute The Visa Co's content and brand marketing strategy. You'll own the voice and identity of our brand across all channels — from social media to thought leadership to campaigns.",
    requirements: "Strong content strategy and copywriting skills. Experience managing brand identity and editorial calendars. Proficiency with social media management and content analytics tools.",
    role: "Develop content strategy, produce high-quality content across all formats, manage brand guidelines, collaborate with design, and measure content performance.",
    idealCandidate: "You are a gifted storyteller with a strong sense of brand. You write beautifully, think strategically, and know how to build audiences that care.",
  },

  // Product
  {
    id: "p001",
    title: "Senior Product Manager",
    department: "product",
    departmentLabel: "Product",
    location: "Dubai, UAE",
    type: "Full-time",
    experience: "5–8 years",
    postedDate: "2025-06-01",
    description: "Lead product discovery and delivery for core areas of The Visa Co platform. You'll define the roadmap, work closely with engineering and design, and obsess over customer problems.",
    requirements: "Proven PM experience at a high-growth B2C or B2B product company. Strong analytical skills, excellent stakeholder management, and a track record of shipping impactful products.",
    role: "Own the product roadmap for your area, run discovery cycles, write clear specs, collaborate with engineering and design, and measure outcomes rigorously.",
    idealCandidate: "You are deeply customer-centric, data-informed, and able to balance strategic thinking with a bias for action. You love building products that genuinely make life easier.",
  },
  {
    id: "p002",
    title: "UX / Product Designer",
    department: "product",
    departmentLabel: "Product",
    location: "Remote / Global",
    type: "Full-time",
    experience: "3–5 years",
    postedDate: "2025-05-23",
    description: "Shape the end-to-end user experience of The Visa Co's products. You'll conduct user research, craft intuitive flows, build our design system, and collaborate closely with product and engineering.",
    requirements: "Proficiency in Figma. Strong portfolio demonstrating end-to-end product design. Experience in user research methodologies. Ability to design for complex workflows clearly and elegantly.",
    role: "Lead design for key product areas, conduct user interviews and usability tests, contribute to and evolve our design system, and advocate for users in every product decision.",
    idealCandidate: "You balance aesthetics with function, love talking to users, and can turn complexity into simplicity. You care deeply about the details that make a product feel right.",
  },
];

// ---- SORT OPTIONS ----
export const SORT_OPTIONS = [
  { value: "newest", label: "Newest " },
  { value: "oldest", label: "Oldest" },
];

// ---- LOCATION OPTIONS ----
export const LOCATION_OPTIONS = [
  { value: "", label: "All Locations" },
  { value: "dubai", label: "Dubai, UAE" },
  { value: "london", label: "Remote / London" },
  { value: "singapore", label: "Remote / Singapore" },
  { value: "global", label: "Remote / Global" },
];

// ---- JOB TYPE OPTIONS ----
export const JOB_TYPE_OPTIONS = [
  { value: "", label: "All Types" },
  { value: "Full-time", label: "Full-time" },
  { value: "Part-time", label: "Part-time" },
  { value: "Contract", label: "Contract" },
];

// ---- COMPANY VALUES ----
export const COMPANY_VALUES = [
  {
    icon: "globe",
    title: "Global Thinking",
    description: "We operate across borders and cultures, bringing a global perspective to every decision we make and every product we build.",
  },
  {
    icon: "zap",
    title: "Move with Purpose",
    description: "We move fast because our customers' needs are urgent. But we never sacrifice quality or integrity for the sake of speed.",
  },
  {
    icon: "users",
    title: "Radical Ownership",
    description: "Every team member takes full ownership of their work and its outcomes. We trust each other to deliver and to speak up when things aren't right.",
  },
  {
    icon: "lightbulb",
    title: "Relentless Curiosity",
    description: "We ask questions before we assume. We learn continuously, challenge assumptions, and embrace new ideas from wherever they come.",
  },
  {
    icon: "heart",
    title: "Guest Obsession",
    description: "Our customers are at the heart of every decision. We measure our success by their success, and we go the extra mile to earn their trust.",
  },
  {
    icon: "trendingUp",
    title: "Grow Together",
    description: "We invest in our people and celebrate growth — individual, team, and company. We rise together, and every win is a shared one.",
  },
];

// ---- PERKS ----
export const PERKS = [
  { icon: "wifi", title: "Remote Flexibility", description: "Work from anywhere — we hire globally and trust you to do great work." },
  { icon: "plane", title: "Travel Benefits", description: "Annual travel credit and discounted visa services for you and your family." },
  { icon: "book", title: "Learning Budget", description: "A generous annual stipend for courses, conferences, and books." },
  { icon: "shield", title: "Health & Wellness", description: "Comprehensive health insurance and a monthly wellness allowance." },
  { icon: "dollar", title: "Competitive Pay", description: "Market-rate salaries benchmarked globally with regular reviews." },
  { icon: "gift", title: "Equity Ownership", description: "Meaningful equity packages so you share in the success you help build." },
  { icon: "clock", title: "Flexible Hours", description: "Async-friendly culture with flexible hours that fit your life." },
  { icon: "building", title: "Team Retreats", description: "Annual in-person retreats to connect, celebrate, and collaborate." },
  { icon: "users", title: "Inclusive Culture", description: "A diverse, welcoming environment where every voice is heard and respected." },
];

// ---- HOW WE WORK STEPS ----
export const HOW_WE_WORK_STEPS = [
  {
    step: 1,
    title: "Apply Online",
    description: "Submit your application in minutes. No cover letter required — your work speaks for itself.",
  },
  {
    step: 2,
    title: "Initial Screen",
    description: "A friendly 30-min call with our Talent team to learn about your background and what you're looking for.",
  },
  {
    step: 3,
    title: "Skills Assessment",
    description: "A practical, paid assessment tailored to the role — no trick questions, just real work.",
  },
  {
    step: 4,
    title: "Team Interviews",
    description: "Meet your future teammates and hiring manager. We value conversations over performances.",
  },
  {
    step: 5,
    title: "Offer & Onboarding",
    description: "A clear, fair offer with full transparency. Then a structured onboarding to set you up for success.",
  },
  {
    step: 6,
    title: "Continuous Growth",
    description: "From day one, you'll have access to mentorship, learning resources, and a clear career progression path.",
  },
];

// ---- HIRING JOURNEY STEPS (Job Detail) ----
export const HIRING_JOURNEY_STEPS = [
  { step: 1, title: "Application Review", description: "We review every application carefully. You'll hear from us within 5 business days." },
  { step: 2, title: "Talent Screen", description: "A 30-minute video call with our Talent team to discuss your experience and the role." },
  { step: 3, title: "Hiring Manager Interview", description: "An in-depth conversation with the hiring manager about your skills, approach, and fit." },
  { step: 4, title: "Skills Assessment", description: "A practical, paid take-home task or technical interview relevant to the role." },
  { step: 5, title: "Final Round", description: "Meet senior leadership and key collaborators. Learn everything you need." },
  { step: 6, title: "Offer", description: "We move fast. Expect a transparent, competitive offer within 2–3 business days." },
];

// ---- EXPERIENCE OPTIONS ----
export const EXPERIENCE_OPTIONS = [
  { value: "", label: "Select experience level" },
  { value: "0-1", label: "0–1 years" },
  { value: "1-3", label: "1–3 years" },
  { value: "3-5", label: "3–5 years" },
  { value: "5-8", label: "5–8 years" },
  { value: "8+", label: "8+ years" },
];

// ---- AVAILABILITY OPTIONS ----
export const AVAILABILITY_OPTIONS = [
  { value: "", label: "Select availability" },
  { value: "immediately", label: "Immediately" },
  { value: "2weeks", label: "Within 2 weeks" },
  { value: "1month", label: "Within 1 month" },
  { value: "2months", label: "Within 2 months" },
  { value: "3months+", label: "3 months or more" },
];

// ---- WORK PREFERENCE OPTIONS ----
export const WORK_PREFERENCE_OPTIONS = [
  { value: "", label: "Select preference" },
  { value: "remote", label: "Fully Remote" },
  { value: "hybrid", label: "Hybrid" },
  { value: "onsite", label: "On-site" },
  { value: "flexible", label: "Flexible / Open to discuss" },
];

// ---- NAV LINKS ----
export const NAV_LINKS = [
  { label: "Roles", section: "jobs" },
  { label: "Culture", section: "values" },
  { label: "Benefits", section: "perks" },
  { label: "How it Works", section: "how" },
  { label: "Contact", section: "contact" },
];

// ---- UI TEXT CONTENT ----
export const TEXTS = {
  // Nav
  OPENINGS_CTA: "Openings",

  // Hero
  HERO_TITLE: "Build Your Future",
  HERO_DESC: "We're making global travel simpler, faster. Join a team of world-class builder and problem-solvers who are redefining how the world moves.",
  EXPLORE_ROLES_CTA: () => "Explore",
  OUR_CULTURE_BTN: "Our Culture",

  // Jobs section
  SECTION_TITLE_FIND_ROLE: "Find Your Next Role",
  SECTION_SUBTITLE_BROWSE: "Browse opportunities across all departments.",
  SEARCH_PLACEHOLDER: "Search by role, department",
  SEARCH_BTN: "Search",
  FILTER_LABEL: "Filter by:",
  SORT_LABEL: "Sort:",
  SHOWING_RESULTS: (count, total) => `Showing ${count} of ${total} roles`,
  NO_ROLES_TITLE: "No roles found",
  NO_ROLES_DESC: "Try adjusting your search or filter criteria.",

  // Values
  SECTION_TITLE_VALUES: "What We Stand For",
  SECTION_DESC_VALUES: "Our values aren't poster words — they're the principles that guide how we work, make decisions, and treat each other.",

  // Perks
  SECTION_TITLE_PERKS: `Life at ${COMPANY_NAME}`,
  SECTION_DESC_PERKS: "We believe great work starts with great conditions. Here's what we offer everyone who joins our team.",

  // How
  SECTION_TITLE_HOW: "How Hiring Works",
  SECTION_DESC_HOW: "We've designed our hiring process to be transparent, respectful of your time, and genuinely two-way.",

  // Contact
  CONTACT_TITLE: "Have Questions?",
  CONTACT_DESC: "Not sure which role is right for you? Want to learn more before you apply? Our Talent team is happy to chat. Reach out — we respond to every message.",
  LINKEDIN_TEXT: "LinkedIn Page",

  // Form
  FORM_ALERT_REQUIRED: "Please fill in all required fields and upload your resume.",
  SUCCESS_TITLE: "Application Submitted!",
  BACK_TO_ROLES: "Back to All Roles",
  FORM_TITLE: "Submit Your Application",
  FORM_SUBTITLE: "We review every application carefully. Fields marked with * are required.",
  FORM_SECTION_PERSONAL: "Personal Information",
  FORM_LABEL_FULLNAME: "Full Name",
  FORM_PLACEHOLDER_FULLNAME: "Jane Smith",
  FORM_LABEL_EMAIL: "Email Address",
  FORM_PLACEHOLDER_EMAIL: "jane@example.com",
  FORM_LABEL_PHONE: "Phone Number",
  FORM_PLACEHOLDER_PHONE: "+971 50 000 0000",

  FORM_SECTION_ADDITIONAL: "Additional Information",
  FORM_LABEL_LINKEDIN: "LinkedIn Profile URL",
  FORM_PLACEHOLDER_LINKEDIN: "https://linkedin.com/in/yourname",
  FORM_LABEL_EXPERIENCE: "Years of Experience",
  FORM_LABEL_AVAILABILITY: "Availability to Start",
  FORM_LABEL_COMPENSATION: "Current Compensation (Annual)",
  FORM_PLACEHOLDER_COMPENSATION: "e.g. ₹ 10,00,000",
  FORM_LABEL_WORK_PREF: "Work Location Preference",
  FORM_LABEL_COVER_NOTE: (name) => `Why do you want to join ${name}?`,
  FORM_PLACEHOLDER_COVER_NOTE: "Tell us what excites you about this role and our mission...",
  FORM_SUBMIT: "Submit Application",
  FORM_SUBMITTING: "Submitting Application...",
  FORM_PRIVACY_TEXT: "By submitting this form, you agree to our Privacy Policy. Your data will only be used for this application and will not be shared with third parties.",

  // Detail page
  DETAIL_BREADCRUMB_CAREERS: "Careers",
  DETAIL_APPLY_BTN: "Apply",
  DETAIL_ALL_ROLES_BTN: "All Roles",
  SECTION_JOB_DESCRIPTION: "Job Description",
  SECTION_THE_ROLE: "The Role",
  SECTION_REQUIREMENTS: "Job Requirements",
  SECTION_IDEAL_CANDIDATE: "The Ideal Candidate",
  SECTION_HIRING_JOURNEY: "The Hiring Journey",
  SIDEBAR_ROLE_OVERVIEW: "Role Overview",
  SIDEBAR_WHY_COMPANY: (name) => `Why ${name}?`,
  SIDEBAR_REASONS: [
    "Equity from day one",
    "Remote-friendly culture",
    "Learning & development",
    "Global impact",
    "Fast-growing team",
  ],

  APPLY_NOW: "Apply Now",

  POSTED_ON: "Posted",
  SIDEBAR_LABELS: ["Department", "Location", "Type", "Experience", "Posted"],
};


